/**
 * Notifications Queries — RTK Query query endpoints
 *
 * Injected into notificationsApi via injectEndpoints.
 */
import { supabase } from "@flexwise/shared/lib/supabaseClient";
import type {
  NotificationPreferences,
  PushSubscriptionRecord,
  NotificationPreviewResponse,
  NotificationHistoryPayload,
  DeliverySnapshotResponse,
  TeacherDeliverySnapshotResponse,
} from "../types/notificationTypes";
import { DEFAULT_PREFERENCES } from "../types/notificationTypes";
import {
  notificationsApi,
  type GetPreferencesArgs,
  type QueueRow,
  type QueueStatusArgs,
  type NotificationSettings,
  type SettingsArgs,
  type PreviewArgs,
} from "./notificationsApi";

const queriesApi = notificationsApi.injectEndpoints({
  endpoints: (builder) => ({
    getPreferences: builder.query<NotificationPreferences, GetPreferencesArgs>({
      async queryFn({ profileId, schoolId }) {
        if (!profileId || !schoolId) {
          return {
            data: {
              user_profile_id: profileId,
              school_id: schoolId,
              ...DEFAULT_PREFERENCES,
            },
          };
        }

        const { data, error } = await supabase
          .from("notification_preferences" as any)
          .select("*")
          .eq("user_profile_id", profileId)
          .maybeSingle();

        if (error) {
          return { error: { status: "CUSTOM_ERROR", error: error.message } };
        }

        if (!data) {
          return {
            data: {
              user_profile_id: profileId,
              school_id: schoolId,
              ...DEFAULT_PREFERENCES,
            },
          };
        }

        const row: Record<string, unknown> = data;

        return {
          data: {
            user_profile_id: row.user_profile_id as string,
            school_id: row.school_id as string,
            push_enabled: (row.push_enabled as boolean) ?? true,
            email_enabled: (row.email_enabled as boolean) ?? false,
            quiet_days: (row.quiet_days as number[]) ?? [],
            quiet_start: (row.quiet_start as string | null) ?? null,
            quiet_end: (row.quiet_end as string | null) ?? null,
            allow_substitution: (row.allow_substitution as boolean) ?? true,
            allow_calendar: (row.allow_calendar as boolean) ?? true,
            allow_school_info: (row.allow_school_info as boolean) ?? true,
            allow_child_updates: (row.allow_child_updates as boolean) ?? true,
          },
        };
      },
      keepUnusedDataFor: 300,
    }),

    getSubscriptions: builder.query<
      PushSubscriptionRecord[],
      GetPreferencesArgs
    >({
      async queryFn({ profileId }) {
        if (!profileId) {
          return { data: [] };
        }

        const { data, error } = await supabase
          .from("push_subscriptions" as any)
          .select(
            "id, user_profile_id, school_id, endpoint, user_agent, created_at",
          )
          .eq("user_profile_id", profileId)
          .order("created_at", { ascending: false });

        if (error) {
          return { error: { status: "CUSTOM_ERROR", error: error.message } };
        }

        return { data: data ?? [] };
      },
      keepUnusedDataFor: 300,
    }),

    getQueueStatus: builder.query<QueueRow | null, QueueStatusArgs>({
      async queryFn({ schoolId, date }) {
        // Priority 1: active row (pending, paused, failed, sending)
        const { data: active, error: activeErr } = await supabase
          .from("notification_send_queue")
          .select("*")
          .eq("school_id", schoolId)
          .eq("target_date", date)
          .in("status", ["pending", "paused", "failed", "sending"])
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (activeErr)
          return { error: { status: 500, data: activeErr.message } };
        if (active) return { data: active as QueueRow };

        // Priority 2: newest historical row (sent, cancelled)
        const { data: latest, error: latestErr } = await supabase
          .from("notification_send_queue")
          .select("*")
          .eq("school_id", schoolId)
          .eq("target_date", date)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (latestErr)
          return { error: { status: 500, data: latestErr.message } };
        return { data: (latest as QueueRow | null) ?? null };
      },
      providesTags: (_res, _err, { date }) => [
        { type: "QueueStatus", id: date },
      ],
    }),

    getNotificationSettings: builder.query<
      NotificationSettings | null,
      SettingsArgs
    >({
      async queryFn({ schoolId }) {
        const { data, error } = await supabase
          .from("notification_settings")
          .select("*")
          .eq("school_id", schoolId)
          .maybeSingle();

        if (error) return { error: { status: 500, data: error.message } };
        return { data: data as NotificationSettings | null };
      },
      providesTags: ["Settings"],
    }),

    previewNotification: builder.query<
      NotificationPreviewResponse,
      PreviewArgs
    >({
      async queryFn({ date, teacherId }) {
        const { data, error } = await supabase.functions.invoke(
          "send-substitution-notifications",
          {
            body: {
              action: "preview",
              date,
              ...(teacherId ? { teacher_id: teacherId } : {}),
            },
          },
        );
        if (error) return { error: { status: 500, data: error.message } };
        return { data: data as NotificationPreviewResponse };
      },
      // Cache is keyed by {date, teacherId} and never refetches on its
      // own — without this tag, publishing/unpublishing a sub would not
      // invalidate the banner's recipient list, and "Keine Lehrkräfte
      // betroffen" would stick around even after new sends are scheduled.
      providesTags: (_res, _err, { date }) => [
        { type: "NotificationPreview", id: date },
      ],
    }),

    getDailyDeliveries: builder.query<DeliveryRow[], { date: string }>({
      async queryFn({ date }) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data, error } = await (supabase as any).rpc(
          "fn_notifications_get_daily_deliveries",
          { p_date: date },
        );
        if (error) return { error: { status: 500, data: error.message } };

        const rows: DeliveryRow[] = ((data as any[]) || []).map((r) => ({
          id: r.id,
          user_profile_id: r.user_profile_id,
          queue_id: r.queue_id ?? null,
          event_key: r.event_key ?? "",
          event_type: r.event_type ?? "",
          first_name: r.first_name ?? "",
          last_name: r.last_name ?? "",
          channel: r.channel as "push" | "email",
          status: r.status as DeliveryRow["status"],
          is_update: r.is_update ?? false,
          sent_at: r.sent_at ?? "",
          read_at: r.read_at ?? null,
          release_at: r.release_at ?? null,
          error_reason: r.error_reason ?? null,
        }));

        return { data: rows };
      },
      providesTags: (_res, _err, { date }) => [
        { type: "DailyDeliveries", id: date },
      ],
    }),

    /**
     * Teacher-accessible: get published substitutions (lesson + Aufsicht)
     * for a given date via single RPC call.
     */
    getTeacherSubstitutionsForDate: builder.query<
      TeacherSubstitutionRow[],
      { profileId: string; date: string }
    >({
      async queryFn({ profileId, date }) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- RPC not in generated types
        const { data, error } = await (supabase as any).rpc(
          "fn_notifications_get_teacher_substitutions",
          { p_teacher_id: profileId, p_date: date },
        );

        if (error) return { error: { status: 500, data: error.message } };

        // RPC returns jsonb array — map to typed rows
        const rows: TeacherSubstitutionRow[] = ((data as any[]) || []).map(
          (r) => ({
            id: r.id,
            entryKind: r.entry_kind as "lesson" | "aufsicht",
            period: r.period || "–",
            className: r.class_name || "–",
            subject: r.subject || "–",
            room: r.room || null,
            absentTeacher: r.absent_teacher || null,
            timeRange: r.time_range || null,
            comment: r.comment || null,
            type: r.sub_type || "vertretung",
            isCancelled: r.is_cancelled ?? false,
            cancelReason: r.cancel_reason || null,
          }),
        );

        return { data: rows };
      },
    }),

    // ── Notification History (Phase 6e — DB-backed) ────────────────────────
    // Args = void → cache key is always undefined (stable single-entry cache).
    // NOT auto-invoked. Hydrated by teacherDashboardApi bootstrap on teacher routes.
    // Lazy-fetched on non-teacher routes only when bell is opened + cache empty.
    getMyNotificationHistory: builder.query<NotificationHistoryPayload, void>({
      async queryFn() {
        const { data, error } = await supabase.rpc(
          "fn_notifications_get_my_history" as any,
          { p_limit: 20 } as any,
        );

        if (error) {
          return {
            error: {
              status: "CUSTOM_ERROR" as const,
              error: error.message,
              data: error,
            },
          };
        }

        const payload = data as unknown as NotificationHistoryPayload;
        return {
          data: {
            items: payload?.items ?? [],
            unread_count: payload?.unread_count ?? 0,
          },
        };
      },
      providesTags: ["NotificationHistory"],
    }),

    /**
     * Admin-only: get immutable send-time snapshot for a specific
     * queue + teacher combination.
     */
    getDeliverySnapshot: builder.query<
      DeliverySnapshotResponse,
      { queueId: string; userProfileId: string }
    >({
      async queryFn({ queueId, userProfileId }) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data, error } = await (supabase as any).rpc(
          "fn_notifications_get_delivery_snapshot",
          { p_queue_id: queueId, p_user_profile_id: userProfileId },
        );
        if (error) return { error: { status: 500, data: error.message } };
        const snap = data as DeliverySnapshotResponse;
        return {
          data: {
            entries: snap?.entries ?? [],
            diff_entries: snap?.diff_entries ?? undefined,
            push_preview: snap?.push_preview ?? null,
            email_preview: snap?.email_preview ?? null,
            channels: snap?.channels ?? [],
            is_update: snap?.is_update ?? false,
          },
        };
      },
    }),

    /**
     * Teacher-safe: get own delivery snapshot by queue_id.
     * Used when opening notification detail from history or push-click.
     */
    getMyDeliverySnapshot: builder.query<
      TeacherDeliverySnapshotResponse,
      { queueId: string }
    >({
      async queryFn({ queueId }) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { data, error } = await (supabase as any).rpc(
          "fn_notifications_get_my_delivery_snapshot",
          { p_queue_id: queueId },
        );
        if (error) return { error: { status: 500, data: error.message } };
        const snap = data as TeacherDeliverySnapshotResponse;
        return {
          data: {
            entries: snap?.entries ?? [],
            diff_entries: snap?.diff_entries ?? undefined,
            push_preview: snap?.push_preview ?? null,
            email_preview: snap?.email_preview ?? null,
            channels: snap?.channels ?? [],
            is_update: snap?.is_update ?? false,
          },
        };
      },
    }),
  }),
  overrideExisting: false,
});

/** Row shape returned by getTeacherSubstitutionsForDate */
export interface TeacherSubstitutionRow {
  id: string;
  entryKind: "lesson" | "aufsicht";
  period: string;
  className: string;
  subject: string;
  room: string | null;
  absentTeacher: string | null;
  timeRange: string | null;
  comment: string | null;
  type: string;
  isCancelled: boolean;
  cancelReason: string | null;
}

/** Row shape returned by getDailyDeliveries */
export interface DeliveryRow {
  id: string;
  user_profile_id: string;
  queue_id: string | null;
  event_key: string;
  event_type: string;
  first_name: string;
  last_name: string;
  channel: "push" | "email";
  status: "sent" | "failed" | "held" | "skipped_dedup" | "skipped_pref";
  is_update: boolean;
  sent_at: string;
  read_at: string | null;
  release_at: string | null;
  error_reason: string | null;
}

export const {
  useGetPreferencesQuery,
  useGetSubscriptionsQuery,
  useGetQueueStatusQuery,
  useGetNotificationSettingsQuery,
  useLazyPreviewNotificationQuery,
  usePreviewNotificationQuery,
  useGetDailyDeliveriesQuery,
  useGetTeacherSubstitutionsForDateQuery,
  useGetMyNotificationHistoryQuery,
  useLazyGetMyNotificationHistoryQuery,
  useGetDeliverySnapshotQuery,
  useGetMyDeliverySnapshotQuery,
} = queriesApi;
